import React, { useState, useMemo } from 'react';
import { Search, ExternalLink, Filter, Tag } from 'lucide-react';
import toolsData from '../data/Tools.js';
import '../CSS/AITools.css';

const AITools = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  // Categories
  const categories = useMemo(
    () => ['All', ...new Set(toolsData.map(t => t.category))],
    []
  );

  // Apply search
  const searchedTools = useMemo(() =>
    toolsData.filter(tool => {
      const q = searchTerm.toLowerCase();
      return (
        tool.name.toLowerCase().includes(q) ||
        tool.description.toLowerCase().includes(q) ||
        tool.tags.some(tag => tag.toLowerCase().includes(q))
      );
    }), [searchTerm]
  );

  // Filtering logic
  const filteredTools = useMemo(() => {
    if (selectedCategory === 'All') {
      // ✅ Only 1 per category (max 6)
      const seen = new Set();
      return searchedTools.filter(tool => {
        if (!seen.has(tool.category)) {
          seen.add(tool.category);
          return true;
        }
        return false;
      }).slice(0, 9);
    } else {
      // ✅ All tools from selected category
      return searchedTools.filter(tool => tool.category === selectedCategory);
    }
  }, [searchedTools, selectedCategory]);

  return (
    <section id="tools" className="tools-section">
      <div className="tools-container">
        <div className="tools-header">
          <h2>AI <span className="gradient-text">Tool Directory</span></h2>
          <p>Curated collection of the best AI tools to supercharge your productivity, creativity, and business operations.</p>
        </div>

        {/* Search Bar */}
        <div className="search-bar">
          <Search size={20} />
          <input
            placeholder="Search tools, features, or tags..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Category Filter */}
        <div className="category-filter">
          <Filter size={16} className="filter-icon" />
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={selectedCategory === cat ? 'active' : ''}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Tools Grid */}
        <div className="tools-grid">
          {filteredTools.map((tool, idx) => (
            <div key={tool.name} className="tool-card" style={{ animationDelay: `${idx * 0.1}s` }}>
              <div className="tool-header">
                <h3 className="tool-name">{tool.name}</h3>
                {/* ✅ Removed category */}
                <a
                  href={tool.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="tool-cta"
                  aria-label={`Open ${tool.name}`}
                >
                  <ExternalLink size={18} />
                </a>
              </div>

              {/* Optional description (if you want) */}
              {/* <p className="tool-desc">{tool.description}</p> */}

              <div className="tool-tags">
                {tool.tags.slice(0, 3).map(tag => (
                  <span key={tag}><Tag size={10} />{tag}</span>
                ))}
                {tool.tags.length > 3 && <span>+{tool.tags.length - 3} more</span>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AITools;
